import React from 'react';
import { SearchableList, Tooltip, TextButton } from '@ds';
import { DSArrowDownIcon, DSPlusIcon } from '@design-system/icons';
import { usePopoverState } from '@shared/hooks';
import { Popover } from '@material-ui/core';
import styles from './ViewSelect.module.scss';
import ViewSelectOption from './ViewSelectOption';
import ViewSelectAutogenOption from './ViewSelectAutogenOption';
import { ViewItemData, ViewSelectOptionList } from './utils';
import ViewSelectActions from './ViewSelectActions';

export type ViewSelectProps = {
  optionList: ViewSelectOptionList[];
  selectedViewId?: string | null;
  onRegenerateDefaultView: () => void;
  onCreateNewView: () => void;
  onSelectView: (viewId: string, data: ViewItemData) => void;
  onEditView: (viewId: string) => void;
  onDuplicateView: (viewId: string) => void;
  onSetDefaultView: (viewName: string, viewId?: string) => void;
  onDeleteView: (viewId: string) => void;
  indicatorLabel: string;
  actionsDisabled?: boolean;
  disabled?: boolean;
};

const MAX_HEIGHT_LIST = window.innerHeight - 310;

const ViewSelect: React.FC<ViewSelectProps> = ({
  optionList,
  selectedViewId,
  onSelectView,
  onCreateNewView,
  onEditView,
  onDuplicateView,
  onSetDefaultView,
  onDeleteView,
  onRegenerateDefaultView,
  indicatorLabel,
  actionsDisabled,
  disabled
}) => {
  const {
    openPopover,
    onClosePopover,
    closePopover,
    isPopoverOpened,
    anchorElement
  } = usePopoverState();

  const flatOptionList = optionList.map(group => group.itemList).flat();
  const selectedOption =
    flatOptionList.find(opt => opt.id === selectedViewId) || flatOptionList[0];
  const selectedLabel = selectedOption.name;

  const onCreateView: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault();
    onCreateNewView();
  };

  const onSelect = (viewId: string, data: ViewItemData) => {
    onSelectView(viewId, data);
    closePopover();
  };

  const ViewIndicator = () => (
    <Tooltip content={indicatorLabel} className={styles.viewIndicator}>
      <></>
    </Tooltip>
  );

  return (
    <>
      <Tooltip
        content={selectedLabel}
        wrapperDisplay="block"
        className={styles.viewBtnContainer}
        disableListeners={selectedOption?.frozen}
      >
        <TextButton
          onClick={openPopover}
          active={isPopoverOpened}
          type="primary"
          size="medium"
          PostfixIcon={<DSArrowDownIcon />}
          data-test="view-select-button"
          disabled={disabled}
        >
          <span className={styles.viewBtnPrefix}>View:</span>
          {selectedLabel}
          {selectedOption?.frozen && <ViewIndicator />}
        </TextButton>
      </Tooltip>
      <Popover
        id="view-select-popover"
        data-test="view-select-popover"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          className: styles.viewSelectorContainer
        }}
        elevation={0}
        transitionDuration={0}
        open={isPopoverOpened}
        anchorEl={anchorElement}
        onClose={onClosePopover}
      >
        <SearchableList itemList={optionList} searchField="name">
          <SearchableList.Result
            maxHeight={MAX_HEIGHT_LIST}
            stickyHeadersEnabled
          >
            {(item: ViewItemData) => {
              if (item.isAutogenerated) {
                return (
                  <ViewSelectAutogenOption
                    onRegenerateDefaultView={onRegenerateDefaultView}
                    selectedViewId={selectedViewId}
                    data={item}
                    onClick={() => onSelect(item.id, item)}
                    actionList={
                      <ViewSelectActions
                        isDefault={item.isDefault}
                        onDuplicateView={() => onDuplicateView(item.id)}
                        onSetDefaultView={() =>
                          onSetDefaultView('Personal view')
                        }
                        duplicateDisableTooltip="To duplicate a view automatically generated by Comet, switch to it first"
                        deleteDisabledTooltip="A personal view, automatically generated by Comet, cannot be deleted"
                        editDisabledTooltip="A personal view, automatically generated by Comet, cannot be edited"
                        disabled={actionsDisabled}
                        disabledActions={item.disabledActions}
                      />
                    }
                    viewIndicator={<ViewIndicator />}
                  />
                );
              }

              return (
                <ViewSelectOption
                  selectedViewId={selectedViewId}
                  data={item}
                  onClick={() => onSelect(item.id, item)}
                  actionList={
                    <ViewSelectActions
                      isDefault={item.isDefault}
                      onEditView={() => onEditView(item.id)}
                      onDuplicateView={() => onDuplicateView(item.id)}
                      onSetDefaultView={() =>
                        onSetDefaultView(item.name, item.id)
                      }
                      onDeleteView={() => onDeleteView(item.id)}
                      disabled={actionsDisabled}
                      disabledActions={item.disabledActions}
                    />
                  }
                />
              );
            }}
          </SearchableList.Result>
          <SearchableList.NotFound />
          {!actionsDisabled && (
            <SearchableList.Footer>
              <SearchableList.FooterActionLink
                text="Create new view"
                to="#"
                onClick={onCreateView}
                PrefixIcon={<DSPlusIcon />}
                data-test="view-select-create-empty-view-button"
              />
            </SearchableList.Footer>
          )}
        </SearchableList>
      </Popover>
    </>
  );
};

export default ViewSelect;
